import { useState, useEffect, useRef, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { BACKEND_URL } from '@/lib/backend';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Monitor, Maximize2, Power, Lock, LogOut, RotateCcw, Keyboard, Upload, X, ScreenShare } from 'lucide-react';
import { toast } from 'sonner';

// Full remote-desktop viewer: renders JPEG frames on a canvas, captures
// mouse/keyboard and relays them to the agent over the WebSocket gateway.
export const RemoteDesktop = ({ device, ticketId, record = false, onClose }) => {
  const [status, setStatus] = useState('connecting');
  const [monitors, setMonitors] = useState([{ id: 0, name: 'Primary' }]);
  const [monitor, setMonitor] = useState(0);
  const [policy, setPolicy] = useState({});
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const sidRef = useRef(null);
  const frameSize = useRef({ w: 1280, h: 720 });
  const startedRef = useRef(null);

  const send = (obj) => { if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj)); };

  const connect = useCallback(async () => {
    setStatus('connecting');
    try {
      const { data } = await rmmAPI.startRemote(device.id, { ticket_id: ticketId, monitor, record });
      sidRef.current = data.session_id;
      setPolicy(data.policy || {});
      setRecording(!!data.recording);
      // Build the viewer WebSocket URL from the public backend origin (the server's own
      // request.base_url is the internal cluster host behind the ingress and is unreachable
      // from the browser). Fall back to the server-provided url only if we must.
      let wsUrl = data.ws_url;
      if (data.viewer_token && (BACKEND_URL || '').startsWith('http')) {
        wsUrl = `${BACKEND_URL.replace(/^http/, 'ws').replace(/\/$/, '')}/api/rmm/remote/ws/viewer/${data.session_id}?token=${encodeURIComponent(data.viewer_token)}`;
      }
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      ws.onopen = () => { setStatus('waiting'); startedRef.current = Date.now(); };
      ws.onmessage = (e) => {
        if (typeof e.data === 'string') {
          try {
            const m = JSON.parse(e.data);
            if (m.t === 'monitors') setMonitors(m.list || [{ id: 0, name: 'Primary' }]);
            if (m.t === 'size') frameSize.current = { w: m.w, h: m.h };
            if (m.t === 'clipboard' && policy.allow_clipboard) navigator.clipboard?.writeText(m.text || '');
          } catch { /* ignore */ }
          return;
        }
        setStatus('live');
        const blob = new Blob([e.data], { type: 'image/jpeg' });
        const img = new Image();
        img.onload = () => {
          const c = canvasRef.current; if (!c) return;
          if (c.width !== img.width) { c.width = img.width; c.height = img.height; frameSize.current = { w: img.width, h: img.height }; }
          c.getContext('2d').drawImage(img, 0, 0);
          URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(blob);
      };
      ws.onclose = () => setStatus((s) => (s === 'live' || s === 'waiting' ? 'disconnected' : s));
      ws.onerror = () => setStatus('error');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start remote session');
      setStatus('error');
    }
  }, [device.id, ticketId, monitor, record, policy.allow_clipboard]);

  useEffect(() => { connect(); return () => { wsRef.current?.close(); }; }, []); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(() => { if (startedRef.current && status === 'live') setDuration(Math.floor((Date.now() - startedRef.current) / 1000)); }, 1000);
    return () => clearInterval(t);
  }, [status]);

  const end = () => {
    if (sidRef.current) rmmAPI.endRemote(sidRef.current).catch(() => {});
    wsRef.current?.close();
    onClose?.();
  };

  const relCoords = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: Math.round(((e.clientX - r.left) / r.width) * frameSize.current.w), y: Math.round(((e.clientY - r.top) / r.height) * frameSize.current.h) };
  };
  const onMouseMove = (e) => { const p = relCoords(e); send({ t: 'mouse', a: 'move', ...p }); };
  const onMouseDown = (e) => { const p = relCoords(e); send({ t: 'mouse', a: 'down', b: e.button, ...p }); };
  const onMouseUp = (e) => { const p = relCoords(e); send({ t: 'mouse', a: 'up', b: e.button, ...p }); };
  const onWheel = (e) => send({ t: 'mouse', a: 'wheel', d: e.deltaY });
  const onKey = (e) => { e.preventDefault(); send({ t: 'key', a: e.type === 'keydown' ? 'down' : 'up', code: e.code, key: e.key }); };

  const doCmd = (t) => { send({ t }); toast.success(`Sent ${t.toUpperCase()} to ${device.hostname}`); };
  const fullscreen = () => canvasRef.current?.parentElement?.requestFullscreen?.();
  const uploadFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error('Max 5 MB over remote transfer');
    const reader = new FileReader();
    reader.onload = () => {
      send({ t: 'file', meta: { name: f.name, dir: 'to_device', size: f.size }, data: reader.result.split(',')[1] });
      toast.success(`Sent ${f.name} to device`);
    };
    reader.readAsDataURL(f);
  };

  const statusColor = { live: 'bg-emerald-500', waiting: 'bg-amber-500', connecting: 'bg-amber-500', disconnected: 'bg-red-500', error: 'bg-red-500' }[status] || 'bg-slate-400';
  const mm = String(Math.floor(duration / 60)).padStart(2, '0'), ss = String(duration % 60).padStart(2, '0');

  return (
    <Dialog open onOpenChange={end}>
      <DialogContent className="max-w-6xl w-[95vw] p-0 bg-slate-900 border-slate-700 overflow-hidden" data-testid="remote-desktop-dialog">
        <DialogTitle className="sr-only">Remote control of {device.hostname}</DialogTitle>
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 text-slate-100">
          <div className="flex items-center gap-2 text-sm">
            <ScreenShare className="w-4 h-4 text-cyan-400" />
            <span className="font-semibold">{device.hostname}</span>
            <span className={`w-2 h-2 rounded-full ${statusColor}`} />
            <span className="text-slate-400 capitalize" data-testid="remote-status">{status}</span>
            {status === 'live' && <span className="text-slate-400" data-testid="remote-duration">· {mm}:{ss}</span>}
            {recording && <Badge className="bg-red-500/20 text-red-300 border-red-500/30 ml-1 animate-pulse" data-testid="remote-recording-badge">● REC</Badge>}
          </div>
          <div className="flex items-center gap-1.5">
            {monitors.length > 1 && (
              <select value={monitor} onChange={(e) => { setMonitor(Number(e.target.value)); send({ t: 'monitor', id: Number(e.target.value) }); }}
                className="bg-slate-700 text-slate-100 text-xs rounded px-2 py-1" data-testid="remote-monitor-select">
                {monitors.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>)}
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={() => doCmd('cad')} title="Ctrl+Alt+Del" data-testid="remote-cad-btn"><Keyboard className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={() => doCmd('lock')} title="Lock" data-testid="remote-lock-btn"><Lock className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={() => doCmd('logoff')} title="Log off" data-testid="remote-logoff-btn"><LogOut className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={() => doCmd('reboot')} title="Reboot" data-testid="remote-reboot-btn"><RotateCcw className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={() => doCmd('shutdown')} title="Shutdown" data-testid="remote-shutdown-btn"><Power className="w-4 h-4" /></Button>
            {policy.allow_file_transfer && (
              <label className="h-7 flex items-center px-2 text-slate-200 hover:bg-slate-700 rounded cursor-pointer" title="Send file" data-testid="remote-file-btn">
                <Upload className="w-4 h-4" /><input type="file" className="hidden" onChange={uploadFile} />
              </label>)}
            <Button size="sm" variant="ghost" className="h-7 text-slate-200 hover:bg-slate-700" onClick={fullscreen} title="Fullscreen" data-testid="remote-fullscreen-btn"><Maximize2 className="w-4 h-4" /></Button>
            <Button size="sm" variant="destructive" className="h-7" onClick={end} data-testid="remote-end-btn"><X className="w-4 h-4 mr-1" />End</Button>
          </div>
        </div>
        <div className="relative bg-black flex items-center justify-center" style={{ minHeight: '60vh' }}
          tabIndex={0} onKeyDown={onKey} onKeyUp={onKey} data-testid="remote-canvas-wrap">
          {(status === 'connecting' || status === 'waiting') && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              <p className="text-sm">{status === 'connecting' ? 'Authorizing session…' : 'Waiting for the device agent to connect…'}</p>
            </div>)}
          {(status === 'disconnected' || status === 'error') && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-3">
              <Monitor className="w-8 h-8 text-red-400" />
              <p className="text-sm">Connection lost.</p>
              <Button size="sm" className="bg-cyan-600" onClick={connect} data-testid="remote-reconnect-btn">Reconnect</Button>
            </div>)}
          <canvas ref={canvasRef} className="max-w-full max-h-[80vh] cursor-crosshair"
            onMouseMove={onMouseMove} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onWheel={onWheel} onContextMenu={(e) => e.preventDefault()}
            data-testid="remote-canvas" />
        </div>
        <div className="px-4 py-1.5 bg-slate-800 text-[11px] text-slate-400 flex items-center gap-3">
          <span>Click the screen then type to control · unattended (no local approval needed)</span>
          {!policy.allow_clipboard && <Badge className="bg-slate-700 text-slate-300">clipboard off</Badge>}
          {!policy.allow_file_transfer && <Badge className="bg-slate-700 text-slate-300">file transfer off</Badge>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
